import mixpanel, { Config } from 'mixpanel-browser'
import posthog from 'posthog-js'

import { UserRoles } from '@types'

type Person = {
  name: string
  email: string
  companyID: string
  isAdmin: boolean
  role: UserRoles
}

const defaultConfig = {
  ignore_dnt: true,
  persistence: 'localStorage',
  debug: false,
}

export class Analytics {
  private readonly key: string
  private readonly name: string
  private storedIdentity: string

  constructor(key: string, name: string = 'default', config?: Partial<Config>) {
    this.key = key
    this.name = name
    this.storedIdentity = ''

    const mixpanelConfig = {
      ...defaultConfig,
      ...(config ?? {}),
    } as Config

    if (this.key) {
      mixpanel.init(key, mixpanelConfig, name)
    }
  }

  // Posthog event with group inclusion
  private posthogGroupEvent(
    label: string,
    group?: Record<string, string[]>,
    props?: Record<string, any>
  ): void {
    const params = {
      ...(props ?? {}),
      ...(group ? { $groups: group } : {}),
    }

    posthog.capture(label, params)
  }

  // IDENTIFY
  // --------------------------------------------------------------------------
  identify(id: string, person: Person): void {
    if (this.storedIdentity === id) return

    this.storedIdentity = id

    posthog.identify(id, person)

    if (this.key) {
      mixpanel[this.name].identify(id)

      mixpanel[this.name].people.set({
        $name: person.name,
        $email: person.email,
        $company_id: person.companyID,
        $is_admin: person.isAdmin,
        $role: person.role,
      })
    }
  }

  // Create a "persistent group"
  // Will be sent as property in all following events until the group is removed
  // Also sets properties on the group
  // Use it for session wide properties
  setGroup(label: string, value: string, props?: Record<string, any>): void {
    posthog.group(label, value, props)

    if (this.key) {
      if (props) {
        mixpanel[this.name].get_group(label, value).set(props)
      }

      mixpanel[this.name].set_group(label, value)
    }
  }

  // Removes a "persistent group"
  removeGroup(label: string, value?: string): void {
    posthog.resetGroups()

    if (this.key) {
      mixpanel[this.name].remove_group(label, value)
    }
  }

  // Defines Group properties without persisting them in the following events.
  identifyGroup(
    groupKey: string,
    groupValue: string,
    props?: Record<string, any>
  ): void {
    posthog.capture('$groupidentify', {
      $group_type: groupKey,
      $group_key: groupValue,
      $group_set: props,
    })

    if (this.key) {
      mixpanel[this.name].get_group(groupKey, groupValue).set(props)
    }
  }

  // EVENT TRACK
  // --------------------------------------------------------------------------

  // Time Event
  timeEvent(label: string, group?: Record<string, string[]>): void {
    // Posthog doesn't have a time_event so we're manually creating a event
    this.posthogGroupEvent(`${label}_started`, group)

    if (this.key) {
      // With Mixpanel we just all the time_event with the same
      // name of the event it will be counted as a time event
      mixpanel[this.name].time_event(label)
    }
  }

  // Default Track Event
  track(
    label: string,
    props?: Record<string, any>,
    group?: Record<string, string[]>
  ): void {
    this.posthogGroupEvent(label, group, props)

    if (this.key) {
      const params = { ...(props ?? {}), ...(group ?? {}) }
      mixpanel[this.name].track(label, params)
    }
  }

  // Pageview Event
  pageview(
    props?: Record<string, any>,
    group?: Record<string, string[]>
  ): void {
    this.posthogGroupEvent('$pageview', group, props)

    if (this.key) {
      const params = { ...(props ?? {}), ...(group ?? {}) }
      mixpanel[this.name].track_pageview(params)
    }
  }

  // RESET
  // --------------------------------------------------------------------------
  reset(): void {
    this.storedIdentity = ''

    posthog.reset()

    if (this.key) {
      mixpanel[this.name].reset()
    }
  }
}
